import { create } from 'zustand'
import api from '../lib/axios'

const TOKEN_KEY = 'tapio_token'
const USER_KEY = 'tapio_user'

function readStoredUser() {
  try {
    const raw = localStorage.getItem(USER_KEY)
    return raw ? JSON.parse(raw) : null
  } catch {
    return null
  }
}

function persistSession(user, token) {
  if (token) localStorage.setItem(TOKEN_KEY, token)
  if (user) localStorage.setItem(USER_KEY, JSON.stringify(user))
}

function clearSession() {
  localStorage.removeItem(TOKEN_KEY)
  localStorage.removeItem(USER_KEY)
}

export const useAuthStore = create((set, get) => ({
  // Hydratation synchrone depuis localStorage au chargement du module,
  // pour éviter un flash "non connecté" au refresh de page.
  user: readStoredUser(),
  token: localStorage.getItem(TOKEN_KEY) || null,
  loading: false,
  isInitialized: false,

  /* ---------- LOGIN ---------- */
  login: async (email, password) => {
    set({ loading: true })
    try {
      const { data } = await api.post('/login', { email, password })
      const user = data.user
      const token = data.token

      persistSession(user, token)
      set({ user, token, loading: false, isInitialized: true })
      return user
    } catch (err) {
      set({ loading: false })
      throw err
    }
  },

  /* ---------- REGISTER ---------- */
  register: async (payload) => {
    set({ loading: true })
    try {
      const { data } = await api.post('/register', payload)
      const user = data.user
      const token = data.token

      persistSession(user, token)
      set({ user, token, loading: false, isInitialized: true })
      return user
    } catch (err) {
      set({ loading: false })
      throw err
    }
  },

  /* ---------- FETCH /me — source de vérité pour le rôle ----------
     À appeler une fois au démarrage de l'app (voir App.jsx) pour
     éviter qu'un user/role périmé en localStorage reste affiché. */
  fetchMe: async () => {
    const token = get().token || localStorage.getItem(TOKEN_KEY)
    if (!token) {
      set({ isInitialized: true })
      return null
    }
    try {
      const { data } = await api.get('/me')
      const user = data.user
      persistSession(user, token)
      set({ user, token, isInitialized: true })
      return user
    } catch {
      // Token invalide/expiré : l'intercepteur axios a déjà nettoyé
      // le localStorage sur le 401, on aligne juste le state.
      clearSession()
      set({ user: null, token: null, isInitialized: true })
      return null
    }
  },

  /* ---------- LOGOUT ---------- */
  logout: async () => {
    try {
      await api.post('/logout')
    } catch {
      // on déconnecte localement même si l'appel serveur échoue
    } finally {
      clearSession()
      set({ user: null, token: null })
    }
  },

  /* ---------- Helpers de rôle, résilients à la forme exacte ---------- */
  isAdmin: () => {
    const u = get().user
    if (!u) return false
    if (u.is_admin === true) return true
    if (u.role === 'admin' || u.role === 'super_admin') return true
    if (Array.isArray(u.roles) && (u.roles.includes('admin') || u.roles.includes('super_admin'))) return true
    return false
  },

  isSuperAdmin: () => {
    const u = get().user
    if (!u) return false
    if (u.role === 'super_admin') return true
    if (Array.isArray(u.roles) && u.roles.includes('super_admin')) return true
    return false
  },
}))